/**
 * One project, full width: its name and progress, the strip of panels the
 * operator opened, and whichever panel `?sub=` names.
 *
 * Full width and not a dialog, which is the change this file exists to make.
 * Eight panels - two of them tables of invoices and contracts - inside a
 * `max-w-2xl` modal meant a client read their delivery record through a letter
 * box. The same shape as SupportSection's thread: the section above swaps this
 * in for the list, and Back puts the list back.
 *
 * ── THE STRIP IS RADIX, THE PANEL IS NOT ───────────────────────────────────
 *
 * `Tabs`/`TabsList`/`TabsTrigger` give the strip its look and its keyboard
 * behaviour (arrow keys, roving focus), but the panel below is dispatched by
 * hand rather than through `TabsContent`. The open panel is URL state: it has to
 * survive a reload, a bookmark and a link pasted into an e-mail, and Radix's own
 * content state knows nothing about the address bar. Reading it from `?sub=`
 * every render is the same rule the whole hub follows for `?tab=`.
 */
import { useEffect, useState } from "react";
import {
  FileSignature, FileText, Flag, LayoutDashboard, LifeBuoy, ListChecks, Receipt,
  type LucideIcon,
} from "lucide-react";
import { Badge, Card, ScrollFade, Tabs, TabsList, TabsTrigger } from "@wa/ui";
import { useT } from "../../../i18n/I18nProvider";
import { portal, type PortalProjectDetail } from "../../../lib/portal";
import { genericStatusVariant } from "../../../lib/format";
import { DocSkeleton, PageHead } from "../../ui";
import { ProgressBar } from "./ProgressBar";
import { projectTabSpec, resolveSubTab, visibleProjectTabs, type ProjectTabIcon } from "./tabs";
import { OverviewPanel } from "./panels/OverviewPanel";
import { MilestonesPanel } from "./panels/MilestonesPanel";
import { TasksPanel } from "./panels/TasksPanel";
import { LinkedDocsPanel } from "./panels/LinkedDocsPanel";

/** The glyph behind each icon name in `tabs.ts`. Total by type, so a name added
 *  to the registry without a glyph fails the build rather than drawing a gap. */
const TAB_ICON: Record<ProjectTabIcon, LucideIcon> = {
  overview: LayoutDashboard,
  milestones: Flag,
  tasks: ListChecks,
  invoices: Receipt,
  estimates: FileText,
  contracts: FileSignature,
  tickets: LifeBuoy,
};

export function ProjectPage({
  id,
  requestedSub,
  onBack,
  onSelectTab,
}: {
  id: string;
  /** The raw `?sub=`, untrusted - `resolveSubTab` decides what it means. */
  requestedSub: string | null;
  onBack: () => void;
  onSelectTab: (sub: string) => void;
}) {
  const t = useT();
  const [detail, setDetail] = useState<PortalProjectDetail | null>(null);

  useEffect(() => {
    setDetail(null);
    // A project id this reader may not open answers 404, and the answer is to go
    // back to the list rather than to sit on a spinner. The old dialog closed
    // itself for the same reason; here "closing" means clearing the URL, so a
    // stale bookmark does not keep re-opening a project that is not theirs.
    void portal.project(id).then(setDetail).catch(() => onBack());
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  // The intersection of the operator's grant and what this build can paint. An
  // empty list while the detail is in flight is fine: no strip is drawn, and
  // `resolveSubTab` still answers, so the deep link lands the moment it arrives.
  const open = detail ? visibleProjectTabs(detail.tabs) : [];
  const sub = resolveSubTab(requestedSub, open);

  if (!detail) return <DocSkeleton label={t("common.loading")} />;

  return (
    <div>
      <PageHead
        back={{ label: t("portal.projects.back"), onClick: onBack }}
        title={detail.name}
        badge={
          <Badge variant={genericStatusVariant(detail.status)}>{t(`portal.projectStatus.${detail.status}`)}</Badge>
        }
        description={detail.client.name}
      />

      <div className="mb-6 flex max-w-xl items-center gap-3">
        <ProgressBar pct={detail.progressPct} />
        <span className="w-10 shrink-0 text-right text-sm font-semibold tabular-nums">{detail.progressPct}%</span>
      </div>

      <Card className="p-5 md:p-6">
        {open.length > 0 && (
          // The strip scrolls, the page does not. Eight labels do not fit a
          // phone, and a `TabsList` that overflows its parent would widen the
          // document instead - which is the whole portal scrolling sideways, on
          // every tab, because of one card.
          //
          // `ScrollFade` rather than a plain `overflow-x-auto`: it hides the
          // scrollbar and softens only the edge that still has something
          // behind it. A bar under a seven-item strip reads as chrome the
          // card did not ask for, while a fade on an edge with nothing past
          // it would promise a tab that is not there. `deps` carries the open
          // list, because the strip's width changes with the operator's
          // grant and a stale measurement would fade the wrong side.
          <ScrollFade className="-mx-1 mb-6 px-1 pb-1" deps={[open.join(",")]}>
            <Tabs value={sub} onValueChange={onSelectTab}>
              <TabsList className="w-max min-w-full justify-start">
                {open.map((tab) => {
                  const spec = projectTabSpec(tab);
                  if (!spec) return null;
                  const Icon = TAB_ICON[spec.icon];
                  return (
                    <TabsTrigger key={tab} value={tab}>
                      <Icon className="size-4" />
                      {t(spec.labelKey)}
                    </TabsTrigger>
                  );
                })}
              </TabsList>
            </Tabs>
          </ScrollFade>
        )}

        {sub === "overview" && <OverviewPanel detail={detail} />}
        {sub === "milestones" && <MilestonesPanel milestones={detail.milestones} />}
        {sub === "tasks" && <TasksPanel tasks={detail.tasks} />}
        {(sub === "linked-invoices"
          || sub === "linked-estimates"
          || sub === "linked-contracts"
          || sub === "linked-tickets") && (
          <LinkedDocsPanel mode={sub} projectId={detail.id} />
        )}
      </Card>
    </div>
  );
}
